import CustomCurrencyInput from "components/CustomCurrencyInput";
import useNotification from "utils/hooks/useNotification";
import useProductApi from "../../hooks/useProductApi";
import { ApolloQueryResult } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { Button, Form } from "antd";
import { useRef, useState } from "react";
import React from "react";
import { HiPencil } from "react-icons/hi";

interface UpdateProductPricePropsTypes {
  type: string;
  name: number;
  refetchProduct?: (
    variables?: Partial<GetVarTypeID> | undefined,
  ) => Promise<ApolloQueryResult<GetProductResponse>>;
  refetchProducts?: () => void;
}

export default function UpdateProductPrice(
  props: UpdateProductPricePropsTypes,
): React.ReactElement {
  const { name, type, refetchProduct, refetchProducts } = props;
  const { t } = useTranslation();
  const form = Form.useFormInstance();

  const [editPrice, setEditPrice] = useState<boolean>(false);
  const originalPriceRef = useRef<number | string | undefined>(undefined);

  const enterEdit = () => {
    originalPriceRef.current = form.getFieldValue(["prices", name, type]);
    setEditPrice(true);
  };

  const cancelEdit = () => {
    form.setFields([
      {
        name: ["prices", name, type],
        value: originalPriceRef.current,
        errors: [],
      },
    ]);
    setEditPrice(false);
  };

  const { notificationError, notificationSuccessfullySaved } = useNotification();
  const { updateProductPrice, updateProductBuyPrice } = useProductApi();

  const extraInfo = Form.useWatch(["prices", name, "extraInfo"], form);
  const currency = form.getFieldValue(["prices", name, "currency"]);
  const price = Form.useWatch(["prices", name, type], form);
  const currencyCode = extraInfo?.currencyCode;
  const productId = Form.useWatch(["productId"], form);

  const handleOnFinish = async () => {
    let errors: any = [];

    const variables: {
      input: {
        product: string;
        currency: ID;
        net?: number;
        total?: number;
      };
    } = {
      input: {
        product: productId,
        net: Number(price),
        currency,
      },
    };

    if (type === "buyPriceTotal") {
      variables.input.net = undefined;
      variables.input.total = Number(price);
    }

    const mutation = type === "buyPriceTotal" ? updateProductBuyPrice : updateProductPrice;
    const response = await mutation({ variables });

    const key = type === "buyPriceTotal" ? "updateProductBuyPrice" : "updateProductPrice";
    errors = (response?.data as Record<string, { errors?: unknown[] }> | undefined)?.[key]?.errors;

    if (errors?.length > 0) {
      const errorFields = {
        priceNet: "net",
        buyPriceTotal: "buyPriceTotal",
      };

      const relevantError = errors.find(
        (item: Record<string, any>) =>
          item.field === errorFields[type as keyof typeof errorFields] && item.messages?.length > 0,
      );

      if (relevantError) {
        form.setFields([
          {
            name: type,
            value: price,
            errors: relevantError.messages || [],
          },
        ]);
        setEditPrice(true);
      }

      notificationError(errors);
    } else {
      notificationSuccessfullySaved(t("Product price"));
      setEditPrice(false);
      refetchProduct!();
      refetchProducts?.();
    }
  };

  const isEditing = editPrice || !productId;

  const formattedPrice =
    price !== undefined && price !== null && price !== ""
      ? `${Number(price).toLocaleString(undefined, {
          minimumFractionDigits: extraInfo?.decimalPlaces ?? 2,
          maximumFractionDigits: extraInfo?.decimalPlaces ?? 2,
        })}${currencyCode ? ` ${currencyCode}` : ""}`
      : "—";

  if (productId && !isEditing) {
    return (
      <div
        role="button"
        tabIndex={0}
        className="update-product-price__display"
        onClick={enterEdit}
        onKeyDown={e => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            enterEdit();
          }
        }}
      >
        <span className="update-product-price__value">{formattedPrice}</span>
        <HiPencil size={16} className="update-product-price__pencil" />
        <Form.Item name={[name, type]} className="update-product-price__hidden-field">
          {extraInfo?.decimalPlaces !== undefined && (
            <CustomCurrencyInput
              disabled
              decimalPlaces={extraInfo?.decimalPlaces}
              suffix={currencyCode}
            />
          )}
        </Form.Item>
      </div>
    );
  }

  return (
    <div className="update-product-price__editor">
      <Form.Item name={[name, type]} className="update-product-price__field">
        {extraInfo?.decimalPlaces !== undefined && (
          <CustomCurrencyInput
            decimalPlaces={extraInfo?.decimalPlaces}
            suffix={currencyCode}
            onPressEnter={handleOnFinish}
          />
        )}
      </Form.Item>
      {productId && (
        <div className="update-product-price__actions">
          <Button size="small" onClick={cancelEdit}>
            {t("Cancel")}
          </Button>
          <Button type="primary" size="small" onClick={handleOnFinish} disabled={!price}>
            {t("Save short")}
          </Button>
        </div>
      )}
    </div>
  );
}
